import { type CipherObject, type ObjectScope, openObject, sealObject, unbase64url } from "./cryptoV2";

type KeyEpoch = Pick<ObjectScope, "datasetId" | "generation" | "keyVersion">;
type HistoricalKey = { version: number; material: string };
const scopeFor = (scope: KeyEpoch): ObjectScope => ({ ...scope, objectId: "key-history", format: 2 });
export const MAX_KEY_VERSIONS = 128;

// Only holders of the current DEK can unwrap history. Keeping old DEKs does not
// give a revoked holder of an old DEK any way to derive the new one.
export async function openKeyHistory(scope: KeyEpoch, key: CryptoKey, cipher?: CipherObject): Promise<HistoricalKey[]> {
  if (scope.keyVersion === 1 && !cipher) return [];
  if (!cipher) throw new Error("sync-key-version-required");
  if (!Number.isSafeInteger(scope.keyVersion) || scope.keyVersion < 2 || scope.keyVersion > MAX_KEY_VERSIONS)
    throw new Error("sync-key-history-format");
  const value = (await openObject(key, scopeFor(scope), cipher)) as {
    type: string;
    version: number;
    keys: HistoricalKey[];
  };
  if (
    !value ||
    Object.keys(value).sort().join() !== "keys,type,version" ||
    value.type !== "classroom-sync-key-history" ||
    value.version !== 1 ||
    !Array.isArray(value.keys) ||
    value.keys.length !== scope.keyVersion - 1
  )
    throw new Error("sync-key-history-format");
  for (let i = 0; i < value.keys.length; i++) {
    const entry = value.keys[i];
    if (
      !entry ||
      Object.keys(entry).sort().join() !== "material,version" ||
      entry.version !== i + 1 ||
      unbase64url(entry.material, 32).length !== 32
    )
      throw new Error("sync-key-history-format");
  }
  return value.keys;
}

export async function rotateKeyHistory(
  scope: KeyEpoch,
  key: CryptoKey,
  previous: CipherObject | undefined,
  currentMaterial: string,
  nextMaterial: string,
): Promise<CipherObject> {
  if (scope.keyVersion >= MAX_KEY_VERSIONS || unbase64url(currentMaterial, 32).length !== 32)
    throw new Error("sync-key-history-format");
  const keys = await openKeyHistory(scope, key, previous);
  return sealObject(
    nextMaterial,
    scopeFor({ datasetId: scope.datasetId, generation: scope.generation, keyVersion: scope.keyVersion + 1 }),
    {
      type: "classroom-sync-key-history",
      version: 1,
      keys: [...keys, { version: scope.keyVersion, material: currentMaterial }],
    },
  );
}
